import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HttpClient, HttpContext } from '@angular/common/http';

import { environment } from 'src/environments/environment';
import { CuttingList, CuttingListLine } from '~shared/types';
import { SKIP_AUTH } from '~core/interceptors/header.interceptor';
import type { Panel } from '~shared/types/geometry';

/** A cutting list line drawn like a panel of a cabinet; `id` is the line's. */
export type CuttingListPanelDto = Partial<Panel> & {
	id: string;
	cuttingListId: string;
	cuttingListLineId: string;
	partId?: string;
	cardinality: number;
};

@Injectable({ providedIn: 'root' })
export class CuttingListService {
	public baseUrl: string = `${environment.baseUrl}/cuttinglist-api/v1`;

	constructor(private http: HttpClient) {}

	public getCuttingLists(projectId: string): Observable<CuttingList[]> {
		return this.http
			.get<CuttingList[]>(`${this.baseUrl}/project/${projectId}/cuttinglist`);
	}

	public createCuttingList(projectId: string, body): Observable<CuttingList> {
		return this.http
			.post<CuttingList>(`${this.baseUrl}/project/${projectId}/cuttinglist`, body);
	}

	public updateCuttingList(projectId: string, cuttingListId: string, body): Observable<CuttingList> {
		return this.http
			.put<CuttingList>(`${this.baseUrl}/project/${projectId}/cuttinglist/${cuttingListId}`, body);
	}

	public duplicateCuttingList(projectId: string, cuttingListId: string): Observable<CuttingList> {
		return this.http
			.post<CuttingList>(`${this.baseUrl}/project/${projectId}/cuttinglist/${cuttingListId}`, {});
	}

	public deleteCuttingList(projectId: string, cuttingListId: string): Observable<void> {
		return this.http
			.delete<void>(`${this.baseUrl}/cuttinglist/${cuttingListId}`);
	}

	/** One panel per line of the cutting lists of a part. Open without a login, for the previews. */
	public getPartPanels(partId: string): Observable<CuttingListPanelDto[]> {
		return this.http.get<CuttingListPanelDto[]>(`${this.baseUrl}/part/${partId}/cuttinglist/panel`, {
			context: new HttpContext().set(SKIP_AUTH, true),
		});
	}

	/** What a piece of the line looks like, as a panel. */
	public getLinePanel(cuttingListId: string, cuttingListLineId: string): Observable<CuttingListPanelDto> {
		return this.http.get<CuttingListPanelDto>(
			`${this.baseUrl}/cuttinglist/${cuttingListId}/cuttinglistline/${cuttingListLineId}/panel`
		);
	}

	public getCuttingListLines(cuttingListId: string): Observable<CuttingListLine[]> {
		if (cuttingListId === 'temp') {
			return of([]);
		}

		return this.http
			.get<CuttingListLine[]>(`${this.baseUrl}/cuttinglist/${cuttingListId}/cuttinglistlines`);
	}

	public createCuttingListLine(cuttingListId: string, body): Observable<CuttingListLine> {
		return this.http
			.post<CuttingListLine>(`${this.baseUrl}/cuttinglist/${cuttingListId}/cuttinglistline`, body);
	}

	public updateCuttingListLine(cuttingListId: string, cuttingListLineId: string, body): Observable<CuttingListLine> {
		return this.http
			.put<CuttingListLine>(`${this.baseUrl}/cuttinglist/${cuttingListId}/cuttinglistline/${cuttingListLineId}`, body);
	}

	public duplicateCuttingListLine(cuttingListId: string, cuttingListLineId: string): Observable<CuttingListLine> {
		return this.http
			.post<CuttingListLine>(`${this.baseUrl}/cuttinglist/${cuttingListId}/cuttinglistline/${cuttingListLineId}`, {});
	}

	public deleteCuttingListLine(cuttingListId: string, cuttingListLineId: string): Observable<void> {
		return this.http
			.delete<void>(`${this.baseUrl}/cuttinglist/${cuttingListId}/cuttinglistline/${cuttingListLineId}`, {});
	}

	public replaceBoardSku(cuttingListId: string, boardSku: string, body): Observable<string> {
		return this.http
			.put(`${this.baseUrl}/cuttinglist/${cuttingListId}/board/${boardSku}`, body, { responseType: 'text' });
	}

	public replaceBoardSkuByProject(projectId: string, boardSku: string, body): Observable<string> {
		return this.http
			.put(`${this.baseUrl}/project/${projectId}/board/${boardSku}`, body, { responseType: 'text' });
	}

	public replaceBoardByPanelGroupForProject(projectId: string, panelGroup: string, body): Observable<string> {
		return this.http
			.patch(`${this.baseUrl}/project/${projectId}/board/panelgroup/${panelGroup}`, body, { responseType: 'text' });
	}

	public regenerateBomLines(cuttingListId: string): Observable<string> {
		return this.http
			.post(`${this.baseUrl}/cuttinglist/${cuttingListId}/bomitems/regenerate`, {}, { responseType: 'text' });
	}

	public regenerateProjectBomLines(projectId: string): Observable<string> {
		return this.http
			.post(`${this.baseUrl}/project/${projectId}/bomitems/regenerate`, {}, { responseType: 'text' });
	}

	public downloadPNX(projectId: string): Observable<Blob> {
		return this.http.get(`${this.baseUrl}/project/${projectId}/export/pnx`, { responseType: 'blob' });
	}
}
